import { ref } from 'vue'

import type { Ref } from 'vue'

import { classifyFiles } from '@shared/ui/other'

import { useTutorPaymentsImportApi } from '../../../api/tools/tutor-payments/import.hook'

/** Mirrors `App\Constants\File::TUTOR_PAYMENT_EXTENSIONS_WHITELIST` (csv only). */
export const TUTOR_PAYMENTS_ACCEPT: string[] = ['.csv']

/** 80 MB — `App\Constants\File::MAX_SIZE_KB` (81920 KB). */
const TUTOR_PAYMENTS_MAX_SIZE = 80 * 1024 * 1024

interface UseImportTutorPaymentsReturn {
  refFile: Ref<File | null>
  refIsUploading: Ref<boolean>
  refProgress: Ref<number>
  refError: Ref<string | null>
  selectFiles: (files: File[]) => void
  submit: () => void
  reset: () => void
}

export const useImportTutorPayments = (): UseImportTutorPaymentsReturn => {
  const refFile = ref<File | null>(null)
  const refIsUploading = ref<boolean>(false)
  const refProgress = ref<number>(0)
  const refError = ref<string | null>(null)

  const reset = (): void => {
    refFile.value = null
    refProgress.value = 0
    refError.value = null
  }

  const selectFiles = (files: File[]): void => {
    if (refIsUploading.value) {
      return
    }

    const file = files[0]
    if (!file) {
      return
    }

    const { accepted, rejected } = classifyFiles([file], {
      accept: TUTOR_PAYMENTS_ACCEPT,
      maxSize: TUTOR_PAYMENTS_MAX_SIZE,
      remainingSlots: 1,
      existingKeys: new Set<string>(),
    })

    const refusal = rejected[0]
    if (refusal) {
      refError.value = refusal.reason
      refFile.value = null
      return
    }

    const accept = accepted[0]
    if (!accept) {
      return
    }

    refError.value = null
    refFile.value = accept.file
  }

  const submit = (): void => {
    const file = refFile.value
    if (!file || refIsUploading.value) {
      return
    }

    void useTutorPaymentsImportApi({
      file,
      onStart: () => {
        refIsUploading.value = true
        refProgress.value = 0
      },
      onProgress: (percent) => {
        refProgress.value = percent
      },
      onSuccess: () => {
        reset()
      },
      onError: (message) => {
        refError.value = message
      },
      onFinish: () => {
        refIsUploading.value = false
      },
    })
  }

  return {
    refFile,
    refIsUploading,
    refProgress,
    refError,
    selectFiles,
    submit,
    reset,
  }
}
